import { useMediaQuery } from '@jouzen/ecom-components/hooks/useMediaQuery';
import { BREAKPOINTS } from '@jouzen/ecom-components/v3/consts/breakpoints';
import {
  useMotionValueEvent,
  useReducedMotion,
  useScroll,
} from 'framer-motion';
import type { FocusEventHandler } from 'react';
import { useRef, useState } from 'react';

import useDetectKeyboardNavigation from '@/app/hooks/useDetectKeyboardNavigation';

import PopAccuracyComfortSection from './PopAccuracyComfortSection';
import type { PopAccuracyComfortSectionData } from './types';

export interface PopAccuracyComfortProps {
  readonly sections: PopAccuracyComfortSectionData[];
}

const PopAccuracyComfort = ({ sections }: PopAccuracyComfortProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const isMediumScreen = useMediaQuery(`(min-width: ${BREAKPOINTS.medium}px)`);
  const [activeSectionIndex, setActiveSectionIndex] = useState<number>(0);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start start', 'end end'],
  });
  const shouldReduceMotion = useReducedMotion();
  const isNavigatingViaKeyboard = useDetectKeyboardNavigation();

  // Update active section index based on scroll position
  useMotionValueEvent(scrollYProgress, 'change', (latest): void => {
    const newActiveSectionIndex = Math.min(
      Math.floor(latest * sections.length),
      sections.length - 1,
    );
    if (activeSectionIndex !== newActiveSectionIndex) {
      setActiveSectionIndex(newActiveSectionIndex);
    }
  });

  // On mobile where sections stack, sections should remain visible until you scroll up above them
  const isActive = (index: number): boolean =>
    isMediumScreen ? activeSectionIndex === index : activeSectionIndex >= index;

  // Automatically scroll active section into view when navigating via keyboard
  const handleSectionFocus = (
    index: number,
  ): FocusEventHandler<HTMLElement> => {
    return () => {
      if (isNavigatingViaKeyboard && ref.current) {
        const wrapperRect = ref.current.getBoundingClientRect();
        const sectionHeight = wrapperRect.height / sections.length;
        const scrollYTarget =
          wrapperRect.top + window.scrollY + index * sectionHeight;
        if (window.scrollY !== scrollYTarget) {
          window.scrollTo({ top: scrollYTarget });
        }
      }
    };
  };

  return (
    <div
      className="md:relative md:motion-reduce:mt-20"
      data-cy="pop-accuracy-comfort"
      id="pop-accuracy-comfort"
      ref={ref}
    >
      <div className="md:motion-safe:js:absolute md:motion-safe:js:size-full">
        <div className="md:motion-safe:js:sticky md:motion-safe:js:top-0 md:motion-safe:js:h-screen md:motion-safe:js:w-full">
          {sections.map(({ id, ...section }, i) => (
            <PopAccuracyComfortSection
              {...section}
              contentIntroTransitionDelay={
                !shouldReduceMotion
                  ? section.contentIntroTransitionDelay
                  : undefined
              }
              index={i}
              isActive={isActive(i)}
              key={id}
              onFocus={handleSectionFocus(i)}
            />
          ))}
        </div>
      </div>
      <div
        aria-hidden="true"
        className="hidden md:motion-safe:js:block"
        data-cy="pop-accuracy-comfort-spacer"
        style={{ height: `${sections.length * 100}vh` }}
      />
    </div>
  );
};

export default PopAccuracyComfort;
